import './styles.scss'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppleIcon, GooglePlay, CoffeeBeanIcon } from 'Assets/Icons'
import AnalogClock from 'Components/Clock'
import { CoffeeWebContext } from 'Context/CoffeeWebContext'
import MetaTagsComponent from 'Components/MetaTagsComponent'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import useAnalytics from 'Utils/useAnalytics'
import { SCREEN_EVENTS } from 'Utils/Constants/analyticsEvents'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { ROUTE_STRINGS } from 'Utils/Constants'
import CoffeeWebVideo from './CoffeeWebVideo'

const AboutUs = ({ ...props }) => {
  const { t } = useTranslation()
  const { isLoading, setLoading } = useContext(CoffeeWebContext)
  const userDetails = getLocalUserDetails()
  const timestamp = new Date().getTime()
  const history = useHistory()
  const location = useLocation()

  const { locationRef, normalizeUrl, setActive, setShowAboutUsNavLogo } = props
  const { setScreenLog } = useAnalytics()
  const [width, setWidth] = useState(window.innerWidth)
  const [isFromCoffeeQuotes, setIsFromCoffeeQuotes] = useState(false)

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth)

    window.addEventListener('resize', handleResize)

    setScreenLog(SCREEN_EVENTS.ABOUT_US_SCREEN, { userType: userDetails ? 'Registered' : 'Guest' })

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  useEffect(() => {
    const pathName = window.location.pathname

    if (pathName.split('/').filter(Boolean).pop().toLocaleLowerCase() === 'aboutus') {
      setIsFromCoffeeQuotes(true)
    }
  }, [])

  const aboutCoffeeWeb = [
    {
      title: t('COFFEE'),
      subTitle: t('ABOUT_COFFEE'),
      subList: [{ subPoint: t('COUNTRIES_ABOUT_COFFEE') }, { subPoint: t('GLOBAL_MARKET') }]
    },
    {
      title: t('COFFEEWEB'),
      subTitle: t('ABOUT_GLOBAL_COFFEE_INDUSTRY'),
      subList: [{ subPoint: t('ABOUT_COFFEE_PREMIER_SOURCE') }, { subPoint: t('ABOUT_COFFEE_WEB_SITE_FEATURE') }, { subPoint: t('ABOUT_COFFEE_WEB_SITE_INFORMATION') }]
    },
    {
      title: t('BENEFICIAL_FOR_ALL_COFFEE_INDUSTRY_STAKEHOLDERS'),
      subList: [{ subPoint: t('BENEFICIAL_FOR_ALL_COFFEE') }, { subPoint: t('OUR_COMPREHENSIVE_PLATFORM_PROVIDES') }]
    }
  ]

  const handleMenuClick = () => {
    // Check if we're coming from CoffeeWebSocial (has locationRef and normalizeUrl props)
    if (props.locationRef && props.normalizeUrl) {
      // Check for prev parameter in URL first (handles refresh case)
      const params = new URLSearchParams(location.search)
      const prevParam = params.get('prev')
      const tabParam = params.get('tab') // Get the tab parameter to preserve it

      // Use prev param from URL if available, otherwise use locationRef
      // eslint-disable-next-line
      const previousView = prevParam ? props.normalizeUrl(prevParam) : props.locationRef.current ? props.normalizeUrl(props.locationRef.current) : null

      if (previousView) {
        // Determine the correct CoffeeWeb path based on user status
        const coffeeWebPath = userDetails?.id ? ROUTE_STRINGS.coffeeweb : ROUTE_STRINGS.guestCoffeeWeb

        // Build the query parameters, preserving the tab if it exists
        const newParams = new URLSearchParams()

        newParams.set('view', previousView)
        if (tabParam) {
          newParams.set('tab', tabParam)
        }

        history.push(`${coffeeWebPath}?${newParams.toString()}`)

        if (props.setActive) {
          props.setActive(previousView)
        }

        if (props.setShowAboutUsNavLogo) {
          props.setShowAboutUsNavLogo(true)
        }
      } else {
        // Fallback to browser back if no previous view found
        history.goBack()
      }
    } else {
      // Coming from other pages (like CoffeeQuotes), use browser back
      history.goBack()
    }
  }

  return (
    <div className={`about-us-container ${isFromCoffeeQuotes ? 'changed-css' : ''}`} data-testid="about-us">
      {!userDetails && <MetaTagsComponent title={t('ABOUT_COFFEE_WEB_TITLE')} description={t('ABOUT_COFFEE_WEB_DESCRIPTION')} keywords={t('ABOUT_COFFEE_WEB_KEYWORDS')} url="https://coffeeweb.com/aboutUs" h1Content={t('ABOUT_COFFEE_WEB_H1_CONTENT')} />}

      <div className={`about-us-top-section ${userDetails?.id ? '' : 'guest-user-top-section'}`}>
        <div className="about-us-header">{t('ABOUT_COFFEE_WEB')}</div>
        <div onClick={handleMenuClick} className="about-us-close-icon">
          Close
        </div>
      </div>

      <AnalogClock setLoading={setLoading} isLoading={isLoading} />
      <div className="coffee-web-about-drop-clock"></div>
      <div className="coffee-web-about-container">
        <div className="coffee-web-about-mobile-view">
          {width <= 1024 && (
            <div className="world-map-image">
              <CoffeeWebVideo isLoading={isLoading} />
            </div>
          )}
        </div>
        <div className="about-coffee-industry" data-testid="about-coffee-industry-summary">
          {aboutCoffeeWeb.map(({ title, subTitle, secondSubTitle, subList }, index) => (
            <div key={index} className="about-us-title">
              <div className="title">{title}</div>
              <div className="sub-title">{subTitle}</div>
              {secondSubTitle && <div className="secondSubTitle">{secondSubTitle}</div>}
              <div className="sub-points">
                {subList.map(({ subPoint }, ind) => (
                  <div className="each-point" key={ind}>
                    <CoffeeBeanIcon className="coffee-bean-icon" data-testid="coffee-bean-icon" />
                    {/* <img src={CoffeeBeanIcon} className="coffee-bean-icon" alt="coffee-bean" /> */}
                    <span>{subPoint}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="coffee-web-about-drop"></div>
        <div className="coffee-web-about-map-wrapper">
          <div style={{ position: 'sticky', top: '110px' }}>
            {width > 1024 && (
              <div className="world-map-image">
                <CoffeeWebVideo isLoading={isLoading} />
              </div>
            )}
            {window.location.pathname !== '/about-us/mobile' && (
              <div className="coffee-web-app-links">
                <div className="apple-link" data-testid="apple-link">
                  <a href="https://apps.apple.com/in/app/coffeeweb/id1618878403" target="_blank" rel="noreferrer">
                    <AppleIcon fill={'#fff'} title="AppleIcon" style={{ width: '40px', height: '40px' }} />
                    <p>
                      {t('DOWNLOAD_ON_THE')}
                      <br /> <span className="big-txt">{t('APP_STORE')}</span>
                    </p>
                  </a>
                </div>
                <div className="play-store-link" data-testid="playStore-link">
                  <a href="https://play.google.com/store/apps/details?id=com.coffeeweb.app" target="_blank" rel="noreferrer">
                    <img alt="play-store" src={GooglePlay} />
                    <p>
                      {t('GET_IT_ON')} <br /> <span className="big-txt">{t('GOOGLE_PLAY')}</span>
                    </p>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AboutUs
